import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Typography, Avatar, Box, Chip } from '@mui/material';

const CHIP_SX = {
  height: '15px',
  '& .MuiChip-label': { px: 0.75, py: 0.05, fontSize: '0.9rem' },
  color: 'primary.main',
  width: 'fit-content',
  maxWidth: 'none',
  mt: 0.4,
  ml: -0.5,
};

const AVATAR_SX = {
  width: 32,
  height: 32,
  flexShrink: 0,
};

/**
 * EntityLink — links to a named entity with optional avatar and description chip.
 *
 * Props:
 *   type        — 'user' | 'organization' | 'consortium' | 'call' | 'fundingagency' | 'fundingprogram'
 *   id          — entity UUID
 *   name        — display name; omit for avatar-only mode
 *   description — short label shown as a chip below the name (avatar+name mode only)
 *   avatar      — { src, alt, children, sx } — enables avatar rendering
 *   showAvatar  — render the avatar (default false)
 *   variant     — MUI Typography variant (default 'inherit')
 *   color       — text color token (default 'primary')
 *   underline   — MUI Link underline prop (default 'hover')
 *   sx          — merged into the name Typography (avatar modes) or the Link (text-only mode)
 *   onClick     — optional extra click handler
 */
const EntityLink = ({
  type,
  id,
  name,
  description,
  sx = {},
  variant = 'inherit',
  color = 'primary',
  underline = 'hover',
  onClick,
  avatar,
  showAvatar = false,
  ...props
}) => {
  // No-id fallback — render non-linked content
  if (!type || !id) {
    if (showAvatar && avatar) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src={avatar.src} alt={avatar.alt} sx={{ ...AVATAR_SX, ...(avatar.sx || {}) }}>
            {avatar.alt?.[0]}
          </Avatar>
          {name && (
            <Box>
              <Typography variant={variant} sx={sx}>{name}</Typography>
              {description && (
                <Chip
                  label={description.length > 22 ? description.slice(0, 22) + '…' : description}
                  size="small"
                  sx={CHIP_SX}
                />
              )}
            </Box>
          )}
        </Box>
      );
    }
    return <Typography variant={variant} sx={sx}>{name || 'Unknown'}</Typography>;
  }

  const getPath = () => {
    switch (type.toLowerCase()) {
      case 'user':         return `/profile/${id}`;
      case 'organization': return `/organization/${id}`;
      case 'consortium':   return `/consortium/${id}`;
      case 'call':
      case 'fundingcall':
      case 'funding_call': return `/funding/call/${id}`;
      case 'lab':          return `/lab/${id}/`;
      case 'program':
      case 'fundingprogram':
      case 'funding_program': return `/funding/program/${id}`;
      case 'fundingagency':
      case 'funding_agency':                  // no page of its own: the calls
      default:             return `/funding/`;
    }
  };

  const handleClick = (e) => { if (onClick) onClick(e); };

  // ── Avatar-only mode ──────────────────────────────────────────────────────
  if (showAvatar && !name) {
    return (
      <Link
        component={RouterLink}
        to={getPath()}
        onClick={handleClick}
        sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: 'pointer' }}
        {...props}
      >
        <Avatar
          src={avatar?.src}
          alt={avatar?.alt}
          sx={{ ...AVATAR_SX, '&:hover': { opacity: 0.8 }, ...(avatar?.sx || {}), ...sx }}
        >
          {avatar?.alt?.[0] || avatar?.children}
        </Avatar>
      </Link>
    );
  }

  // ── Avatar + name (+ optional description chip) mode ─────────────────────
  if (showAvatar && avatar) {
    const displayName = name && name.length > 20 ? name.slice(0, 20) + '…' : name;
    return (
      <Link
        component={RouterLink}
        to={getPath()}
        onClick={handleClick}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', cursor: 'pointer', flex: 1, minWidth: 0 }}
        {...props}
      >
        <Avatar
          src={avatar.src}
          alt={avatar.alt}
          sx={{ ...AVATAR_SX, '&:hover': { opacity: 0.8 }, ...(avatar.sx || {}) }}
        >
          {avatar.alt?.[0] || avatar.children}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant={variant}
            color={color}
            sx={{
              fontWeight: 500,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&:hover': { color: 'primary.main', textDecoration: underline === 'hover' ? 'underline' : 'none' },
              ...sx,
            }}
          >
            {displayName}
          </Typography>
          {description && (
            <Chip
              label={description.length > 22 ? description.slice(0, 22) + '…' : description}
              size="small"
              sx={CHIP_SX}
            />
          )}
        </Box>
      </Link>
    );
  }

  // ── Text-only mode (default) ──────────────────────────────────────────────
  return (
    <Link
      component={RouterLink}
      to={getPath()}
      variant={variant}
      color={color}
      underline={underline}
      onClick={handleClick}
      sx={{
        fontWeight: 500,
        lineHeight: 1.4,
        cursor: 'pointer',
        '&:hover': { color: 'primary.main', fontWeight: 600 },
        ...sx,
      }}
      {...props}
    >
      {name}
    </Link>
  );
};

export default EntityLink;
